import { LocalCliError } from "./errors.js";
import type { AgentPin } from "./agent-content.js";

/**
 * Reader for `installer/release-pin.json` — the declared claim about what a
 * from-zero install deploys.
 *
 * Schema and field contracts: docs/architecture/AGENT-REPO-CONTRACT.md.
 * The offline lockstep guard (scripts/check-release-pin-lockstep.mjs) checks the
 * SAME invariants against the checked-in file at CI time; this reader enforces
 * them again at the moment of use, because a pin can also arrive from a fetched
 * release rather than from the tree.
 */

export const PIN_SCHEMA_VERSION = 2;

export interface ReleasePin {
  readonly schemaVersion: number;
  readonly platformVersion: string;
  readonly agents: Readonly<Record<string, AgentPin>>;
  /** Personas a from-zero install deploys. A subset of the personas the agents provide. */
  readonly personas: readonly string[];
}

const REF_RE = /^[0-9a-f]{40}$/;
const DIGEST_RE = /^sha256:[0-9a-f]{64}$/;
const REPO_RE = /^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/;

function fail(source: string, detail: string, hint?: string): never {
  throw new LocalCliError({
    code: "RELEASE_PIN_INVALID",
    message: `${source}: ${detail}`,
    ...(hint ? { hint } : {}),
  });
}

function parseAgent(source: string, key: string, raw: unknown): AgentPin {
  if (typeof raw !== "object" || raw === null) fail(source, `agents.${key} is not an object.`);
  const a = raw as Record<string, unknown>;

  if (typeof a.repo !== "string" || !REPO_RE.test(a.repo)) {
    fail(source, `agents.${key}.repo must be "owner/name", got ${JSON.stringify(a.repo)}.`);
  }
  if (typeof a.ref !== "string" || !REF_RE.test(a.ref)) {
    fail(
      source,
      `agents.${key}.ref must be a 40-hex commit sha, got ${JSON.stringify(a.ref)}.`,
      "A tag or branch can move, so it is not a pin. Use `m8t agent-content pin <key>` to resolve one.",
    );
  }
  if (a.visibility !== "public" && a.visibility !== "private") {
    fail(source, `agents.${key}.visibility must be "public" or "private", got ${JSON.stringify(a.visibility)}.`);
  }
  if (typeof a.persona !== "string" || a.persona.length === 0) {
    fail(source, `agents.${key}.persona must be a non-empty persona slug.`);
  }
  if (typeof a.contentSha !== "string" || !DIGEST_RE.test(a.contentSha)) {
    fail(source, `agents.${key}.contentSha must be "sha256:<64-hex>", got ${JSON.stringify(a.contentSha)}.`);
  }
  return { repo: a.repo, ref: a.ref, visibility: a.visibility, persona: a.persona, contentSha: a.contentSha };
}

/** Parse + validate a release pin. `source` names the file in every refusal. */
export function parseReleasePin(json: string, source: string): ReleasePin {
  let doc: unknown;
  try {
    doc = JSON.parse(json);
  } catch (e) {
    fail(source, `not valid JSON: ${(e as Error).message}`);
  }
  if (typeof doc !== "object" || doc === null) fail(source, "top level is not an object.");
  const d = doc as Record<string, unknown>;

  if (d.schemaVersion !== PIN_SCHEMA_VERSION) {
    fail(
      source,
      `schemaVersion ${JSON.stringify(d.schemaVersion)} is not the expected ${PIN_SCHEMA_VERSION.toString()}.`,
      "This CLI is older or newer than the pin it is reading. Guessing at an unknown shape is how a stale pin ships as if it were current.",
    );
  }
  if (typeof d.platformVersion !== "string") fail(source, "platformVersion is missing.");

  // Typed as `unknown` on purpose. `d.agents ?? {}` narrows to `{}`, which makes the
  // shape check below look dead to the linter — but the input is arbitrary JSON, so
  // `agents: "yes"` is entirely possible and must be refused rather than iterated.
  const rawAgents: unknown = d.agents ?? {};
  // Arrays are typeof "object", so `agents: []` would sail through a bare object
  // check and then read as "no agents pinned" — a shape error quietly becoming a
  // plausible-looking empty set.
  if (typeof rawAgents !== "object" || rawAgents === null || Array.isArray(rawAgents)) {
    fail(source, "agents must be an object keyed by agent name.");
  }
  const agents: Record<string, AgentPin> = {};
  const personaOwner = new Map<string, string>();
  for (const [key, raw] of Object.entries(rawAgents as Record<string, unknown>)) {
    const agent = parseAgent(source, key, raw);
    const existing = personaOwner.get(agent.persona);
    if (existing !== undefined) {
      fail(
        source,
        `agents.${existing} and agents.${key} both claim persona '${agent.persona}'.`,
        "Two sources for one persona means hydration order decides which wins — silently.",
      );
    }
    personaOwner.set(agent.persona, key);
    agents[key] = agent;
  }

  const personas = d.personas ?? [];
  if (!Array.isArray(personas) || personas.some((p) => typeof p !== "string")) {
    fail(source, "personas must be an array of strings.");
  }
  // Deliberately NOT checked here: that every entry in `personas` is provided by a
  // pinned agent. A persona can also live in this repo's own tree (cmo does), and this
  // parser may be reading a pin fetched from a release, with no tree to consult. The
  // resolvability check belongs where the tree is observable — the CI lockstep guard —
  // and install-descriptor-gen refuses at build time as the backstop.

  return {
    schemaVersion: d.schemaVersion,
    platformVersion: d.platformVersion,
    agents,
    personas: personas as string[],
  };
}
